import React, { useEffect, useState, useRef } from "react";
import { Hand, Timer } from "lucide-react";

/**
 * IdleGuard — zeigt nach `idleMs` Sekunden Inaktivität eine „Noch da?"-Modal.
 * Wenn innerhalb von `graceMs` niemand reagiert, ruft `onExpire()` auf
 * (z. B. zurück zum Start scrollen oder Wizard verlassen).
 *
 * Nutzt window-Events (touchstart, click, keydown, mousemove).
 */
export default function IdleGuard({ idleMs = 60_000, graceMs = 20_000, onExpire, testId = "idle-guard" }) {
  const [prompt, setPrompt] = useState(false);
  const [remaining, setRemaining] = useState(Math.floor(graceMs / 1000));
  const idleTimerRef = useRef(null);
  const graceTimerRef = useRef(null);
  const graceIntervalRef = useRef(null);

  const clearGrace = () => {
    if (graceTimerRef.current) { clearTimeout(graceTimerRef.current); graceTimerRef.current = null; }
    if (graceIntervalRef.current) { clearInterval(graceIntervalRef.current); graceIntervalRef.current = null; }
    setPrompt(false);
  };

  const startIdle = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      // Prompt zeigen + Countdown starten
      setPrompt(true);
      setRemaining(Math.floor(graceMs / 1000));
      graceIntervalRef.current = setInterval(() => {
        setRemaining((r) => Math.max(0, r - 1));
      }, 1000);
      graceTimerRef.current = setTimeout(() => {
        clearGrace();
        onExpire?.();
      }, graceMs);
    }, idleMs);
  };

  useEffect(() => {
    const reset = () => {
      // Wenn Prompt sichtbar, jeder Klick/Touch dismisst
      if (prompt) {
        clearGrace();
      }
      startIdle();
    };
    startIdle();
    const evs = ["touchstart", "click", "keydown", "mousemove"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      clearGrace();
      evs.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [prompt, idleMs, graceMs, onExpire]);

  if (!prompt) return null;

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center px-6"
      onClick={() => clearGrace()}
      onTouchStart={() => clearGrace()}
    >
      <div className="max-w-md w-full text-center animate-in fade-in zoom-in-95 duration-500 relative">
        {/* Marquee Bulbs */}
        <div className="absolute inset-x-0 -top-6 flex justify-between px-4 pointer-events-none">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_8px_rgba(212,175,55,0.9)] animate-pulse" style={{ animationDelay: `${(i % 4) * 200}ms` }} />
          ))}
        </div>

        <div className="border-2 border-[#D4AF37] bg-[#0A0A0A] rounded-lg p-10">
          <div className="mx-auto w-20 h-20 rounded-full border-2 border-[#D4AF37] flex items-center justify-center mb-6 animate-touch-pulse">
            <Hand size={38} className="text-[#D4AF37]" />
          </div>
          <div className="font-heading text-5xl text-[#F5F0E6] leading-none">Bist du noch da?</div>
          <div className="mt-4 text-[#A8A398] text-sm max-w-sm mx-auto">
            Tippe irgendwo auf den Bildschirm, um weiterzumachen.
          </div>

          <div className="mt-8 flex items-center justify-center gap-3 text-[#D4AF37]">
            <Timer size={18} />
            <span className="font-mono text-4xl tabular-nums" data-testid="idle-countdown">{remaining}</span>
            <span className="text-xs uppercase tracking-[0.4em]">Sek.</span>
          </div>

          <button
            data-testid="idle-continue-btn"
            onClick={(e) => { e.stopPropagation(); clearGrace(); }}
            className="mt-8 px-8 py-4 rounded-full bg-[#D4AF37] text-black text-sm uppercase tracking-[0.35em] hover:bg-[#EEDC82] transition min-h-[64px]"
          >
            Ja, ich bin noch da
          </button>
        </div>

        <style>{`
          @keyframes touchPulse {
            0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(212,175,55,0.55); }
            50% { transform: scale(1.06); box-shadow: 0 0 0 16px rgba(212,175,55,0); }
          }
          .animate-touch-pulse { animation: touchPulse 1.8s ease-in-out infinite; }
        `}</style>
      </div>
    </div>
  );
}
